import React, { createContext, useContext, useEffect, useState } from 'react';
import { IUserPermissions } from '../../shared/models/IUserPermissions';

interface User {
  email: string;
  displayName: string;
  permissions?: IUserPermissions;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Beim Start prüfen ob User eingeloggt ist
  useEffect(() => {
    checkAuth();
  }, []);

  const loadPermissions = async (email: string): Promise<IUserPermissions | undefined> => {
    try {
      const response = await fetch(`/api/permissions/me?email=${encodeURIComponent(email)}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const permissions = await response.json();
        return permissions;
      }
    } catch (error) {
      console.error('[Auth] Fehler beim Laden der Berechtigungen:', error);
    }
    return undefined;
  };

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/auth/me', {
        credentials: 'include', // Session-Cookie mitsenden
      });

      if (response.ok) {
        const data = await response.json();
        const permissions = await loadPermissions(data.user.email);
        setUser({
          ...data.user,
          permissions,
        });
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('[Auth] Fehler beim Prüfen der Authentifizierung:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include', // Session-Cookie speichern
      });

      const data = await response.json();

      if (response.ok) {
        const permissions = await loadPermissions(data.user.email);
        setUser({
          ...data.user,
          permissions,
        });
        return { success: true };
      } else {
        return { success: false, error: data.message || 'Login fehlgeschlagen' };
      }
    } catch (error) {
      console.error('[Auth] Login-Fehler:', error);
      return { success: false, error: 'Netzwerkfehler beim Login' };
    }
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      setUser(null);
    } catch (error) {
      console.error('[Auth] Logout-Fehler:', error);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden');
  }
  return context;
};
